/**
 * IconPicker.jsx
 * 
 * A reusable component for selecting FontAwesome icons.
 * Displays a grid of common icons organized by category with search functionality.
 * 
 * Usage:
 * <IconPicker 
 *     value="fa-file" 
 *     onChange={(icon) => handleIconChange(icon)} 
 *     color="#3498db"
 * />
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import '@/styles/IconPicker.scss';
import { FaTimes, FaIcons, FaChevronUp, FaChevronDown, FaSearch } from 'react-icons/fa';
import { getFaIcon } from '@/utils/faIconMap';

/**
 * Common FontAwesome icons organized by category.
 * These are frequently used icons for templates and UI elements.
 */
const ICON_CATEGORIES = [
    {
        name: 'Actions & Tasks',
        icons: [
            { value: 'fa-tasks', label: 'Tasks' },
            { value: 'fa-check', label: 'Check' },
            { value: 'fa-check-circle', label: 'Check Circle' },
            { value: 'fa-check-square', label: 'Check Square' },
            { value: 'fa-clipboard', label: 'Clipboard' },
            { value: 'fa-clipboard-check', label: 'Clipboard Check' },
            { value: 'fa-clipboard-list', label: 'Clipboard List' },
            { value: 'fa-list', label: 'List' },
            { value: 'fa-list-alt', label: 'List Alt' },
            { value: 'fa-list-ul', label: 'List Unordered' },
            { value: 'fa-list-ol', label: 'List Ordered' },
            { value: 'fa-pen', label: 'Pen' },
            { value: 'fa-edit', label: 'Edit' },
            { value: 'fa-pencil-alt', label: 'Pencil' },
        ]
    },
    {
        name: 'Alerts & Status',
        icons: [
            { value: 'fa-exclamation', label: 'Exclamation' },
            { value: 'fa-exclamation-circle', label: 'Exclamation Circle' },
            { value: 'fa-exclamation-triangle', label: 'Warning Triangle' },
            { value: 'fa-bell', label: 'Bell' },
            { value: 'fa-bell-slash', label: 'Bell Slash' },
            { value: 'fa-flag', label: 'Flag' },
            { value: 'fa-info', label: 'Info' },
            { value: 'fa-info-circle', label: 'Info Circle' },
            { value: 'fa-question', label: 'Question' },
            { value: 'fa-question-circle', label: 'Question Circle' },
            { value: 'fa-times', label: 'Times' },
            { value: 'fa-times-circle', label: 'Times Circle' },
            { value: 'fa-ban', label: 'Ban' },
        ]
    },
    {
        name: 'Documents & Files',
        icons: [
            { value: 'fa-file', label: 'File' },
            { value: 'fa-file-alt', label: 'File Alt' },
            { value: 'fa-file-pdf', label: 'PDF' },
            { value: 'fa-file-excel', label: 'Excel' },
            { value: 'fa-file-word', label: 'Word' },
            { value: 'fa-file-image', label: 'Image File' },
            { value: 'fa-file-contract', label: 'Contract' },
            { value: 'fa-file-invoice', label: 'Invoice' },
            { value: 'fa-file-invoice-dollar', label: 'Invoice Dollar' },
            { value: 'fa-folder', label: 'Folder' },
            { value: 'fa-folder-open', label: 'Folder Open' },
            { value: 'fa-paperclip', label: 'Paperclip' },
            { value: 'fa-receipt', label: 'Receipt' },
        ]
    },
    {
        name: 'Business & Finance',
        icons: [
            { value: 'fa-building', label: 'Building' },
            { value: 'fa-briefcase', label: 'Briefcase' },
            { value: 'fa-chart-line', label: 'Chart Line' },
            { value: 'fa-chart-bar', label: 'Chart Bar' },
            { value: 'fa-chart-pie', label: 'Chart Pie' },
            { value: 'fa-dollar-sign', label: 'Dollar' },
            { value: 'fa-money-bill', label: 'Money Bill' },
            { value: 'fa-money-check', label: 'Money Check' },
            { value: 'fa-credit-card', label: 'Credit Card' },
            { value: 'fa-wallet', label: 'Wallet' },
            { value: 'fa-piggy-bank', label: 'Piggy Bank' },
            { value: 'fa-coins', label: 'Coins' },
            { value: 'fa-percentage', label: 'Percentage' },
            { value: 'fa-calculator', label: 'Calculator' },
        ]
    },
    {
        name: 'People & Communication',
        icons: [
            { value: 'fa-user', label: 'User' },
            { value: 'fa-users', label: 'Users' },
            { value: 'fa-user-tie', label: 'User Tie' },
            { value: 'fa-user-cog', label: 'User Settings' },
            { value: 'fa-user-check', label: 'User Check' },
            { value: 'fa-user-plus', label: 'User Plus' },
            { value: 'fa-handshake', label: 'Handshake' },
            { value: 'fa-comments', label: 'Comments' },
            { value: 'fa-comment', label: 'Comment' },
            { value: 'fa-envelope', label: 'Envelope' },
            { value: 'fa-phone', label: 'Phone' },
            { value: 'fa-address-book', label: 'Address Book' },
            { value: 'fa-id-card', label: 'ID Card' },
            { value: 'fa-headset', label: 'Headset' },
        ]
    },
    {
        name: 'Legal & Compliance',
        icons: [
            { value: 'fa-gavel', label: 'Gavel' },
            { value: 'fa-balance-scale', label: 'Balance Scale' },
            { value: 'fa-shield-alt', label: 'Shield' },
            { value: 'fa-lock', label: 'Lock' },
            { value: 'fa-unlock', label: 'Unlock' },
            { value: 'fa-key', label: 'Key' },
            { value: 'fa-stamp', label: 'Stamp' },
            { value: 'fa-certificate', label: 'Certificate' },
            { value: 'fa-award', label: 'Award' },
            { value: 'fa-user-shield', label: 'User Shield' },
            { value: 'fa-fingerprint', label: 'Fingerprint' },
        ]
    },
    {
        name: 'Operations & Tools',
        icons: [
            { value: 'fa-cogs', label: 'Cogs' },
            { value: 'fa-cog', label: 'Cog' },
            { value: 'fa-wrench', label: 'Wrench' },
            { value: 'fa-tools', label: 'Tools' },
            { value: 'fa-hammer', label: 'Hammer' },
            { value: 'fa-screwdriver', label: 'Screwdriver' },
            { value: 'fa-hard-hat', label: 'Hard Hat' },
            { value: 'fa-industry', label: 'Industry' },
            { value: 'fa-warehouse', label: 'Warehouse' },
            { value: 'fa-truck', label: 'Truck' },
            { value: 'fa-box', label: 'Box' },
            { value: 'fa-boxes', label: 'Boxes' },
            { value: 'fa-dolly', label: 'Dolly' },
        ]
    },
    {
        name: 'Technology & IT',
        icons: [
            { value: 'fa-laptop', label: 'Laptop' },
            { value: 'fa-desktop', label: 'Desktop' },
            { value: 'fa-server', label: 'Server' },
            { value: 'fa-database', label: 'Database' },
            { value: 'fa-hdd', label: 'HDD' },
            { value: 'fa-wifi', label: 'WiFi' },
            { value: 'fa-network-wired', label: 'Network' },
            { value: 'fa-code', label: 'Code' },
            { value: 'fa-bug', label: 'Bug' },
            { value: 'fa-laptop-code', label: 'Laptop Code' },
            { value: 'fa-terminal', label: 'Terminal' },
            { value: 'fa-microchip', label: 'Microchip' },
        ]
    },
    {
        name: 'Environment & Sustainability',
        icons: [
            { value: 'fa-leaf', label: 'Leaf' },
            { value: 'fa-tree', label: 'Tree' },
            { value: 'fa-seedling', label: 'Seedling' },
            { value: 'fa-recycle', label: 'Recycle' },
            { value: 'fa-solar-panel', label: 'Solar Panel' },
            { value: 'fa-wind', label: 'Wind' },
            { value: 'fa-water', label: 'Water' },
            { value: 'fa-tint', label: 'Tint' },
            { value: 'fa-globe', label: 'Globe' },
            { value: 'fa-globe-africa', label: 'Globe Africa' },
            { value: 'fa-temperature-high', label: 'Temperature High' },
            { value: 'fa-thermometer-half', label: 'Thermometer' },
        ]
    },
    {
        name: 'Marketing & Media',
        icons: [
            { value: 'fa-bullhorn', label: 'Bullhorn' },
            { value: 'fa-ad', label: 'Ad' },
            { value: 'fa-broadcast-tower', label: 'Broadcast' },
            { value: 'fa-camera', label: 'Camera' },
            { value: 'fa-video', label: 'Video' },
            { value: 'fa-image', label: 'Image' },
            { value: 'fa-images', label: 'Images' },
            { value: 'fa-photo-video', label: 'Photo Video' },
            { value: 'fa-newspaper', label: 'Newspaper' },
            { value: 'fa-palette', label: 'Palette' },
            { value: 'fa-paint-brush', label: 'Paint Brush' },
        ]
    },
    {
        name: 'Time & Scheduling',
        icons: [
            { value: 'fa-clock', label: 'Clock' },
            { value: 'fa-calendar', label: 'Calendar' },
            { value: 'fa-calendar-alt', label: 'Calendar Alt' },
            { value: 'fa-calendar-check', label: 'Calendar Check' },
            { value: 'fa-calendar-times', label: 'Calendar Times' },
            { value: 'fa-calendar-day', label: 'Calendar Day' },
            { value: 'fa-calendar-week', label: 'Calendar Week' },
            { value: 'fa-hourglass', label: 'Hourglass' },
            { value: 'fa-hourglass-half', label: 'Hourglass Half' },
            { value: 'fa-stopwatch', label: 'Stopwatch' },
            { value: 'fa-history', label: 'History' },
            { value: 'fa-sync', label: 'Sync' },
            { value: 'fa-redo', label: 'Redo' },
        ]
    },
    {
        name: 'Misc & Symbols',
        icons: [
            { value: 'fa-star', label: 'Star' },
            { value: 'fa-heart', label: 'Heart' },
            { value: 'fa-bolt', label: 'Bolt' },
            { value: 'fa-fire', label: 'Fire' },
            { value: 'fa-lightbulb', label: 'Lightbulb' },
            { value: 'fa-trophy', label: 'Trophy' },
            { value: 'fa-medal', label: 'Medal' },
            { value: 'fa-gem', label: 'Gem' },
            { value: 'fa-crown', label: 'Crown' },
            { value: 'fa-bookmark', label: 'Bookmark' },
            { value: 'fa-tag', label: 'Tag' },
            { value: 'fa-tags', label: 'Tags' },
            { value: 'fa-thumbs-up', label: 'Thumbs Up' },
            { value: 'fa-thumbs-down', label: 'Thumbs Down' },
        ]
    },
];

/**
 * Flattened list of all icons for search functionality.
 */
const ALL_ICONS = ICON_CATEGORIES.flatMap(cat => cat.icons);

/**
 * IconPicker Component
 * 
 * @param {string} value - Currently selected icon class (e.g., "fa-file")
 * @param {function} onChange - Callback when icon selection changes
 * @param {string} color - Color to use for icon preview
 * @param {string} placeholder - Placeholder text when no icon selected
 * @param {boolean} disabled - Whether the picker is disabled
 */
const IconPicker = ({ value, onChange, color = '#3498db', placeholder = 'Select an icon...', disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState(null);
    const containerRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Filter icons based on search query
    const filteredIcons = useMemo(() => {
        if (!searchQuery.trim()) return null; // Return null to show categories
        
        const query = searchQuery.toLowerCase();
        return ALL_ICONS.filter(icon => 
            icon.label.toLowerCase().includes(query) || 
            icon.value.toLowerCase().includes(query)
        );
    }, [searchQuery]);

    // Get the current icon's label for display
    const currentIconLabel = useMemo(() => {
        if (!value) return null;
        const found = ALL_ICONS.find(icon => icon.value === value);
        return found ? found.label : value.replace('fa-', '');
    }, [value]);

    /**
     * Handles icon selection from the dropdown.
     * @param {string} iconValue - The selected icon class
     */
    const handleSelect = (iconValue) => {
        onChange(iconValue);
        setIsOpen(false);
        setSearchQuery('');
        setActiveCategory(null);
    };

    /**
     * Clears the selected icon.
     */
    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
    };

    return (
        <div className={`icon-picker ${disabled ? 'disabled' : ''}`} ref={containerRef}>
            {/* Trigger button shows selected icon or placeholder */}
            <div 
                className="icon-picker-trigger"
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {value ? (
                    <div className="selected-icon">
                        {React.createElement(getFaIcon(value), { style: { color }, size: '1.2em' })}
                        <span className="icon-label">{currentIconLabel}</span>
                        {!disabled && (
                            <button 
                                type="button" 
                                className="clear-btn"
                                onClick={handleClear}
                                title="Clear icon"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>
                ) : (
                    <span className="placeholder">
                        <FaIcons /> {placeholder}
                    </span>
                )}
                {isOpen ? <FaChevronUp className="dropdown-arrow" /> : <FaChevronDown className="dropdown-arrow" />}
            </div>

            {/* Dropdown panel with search and icon grid */}
            {isOpen && (
                <div className="icon-picker-dropdown">
                    {/* Search input */}
                    <div className="icon-search">
                        <FaSearch />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setActiveCategory(null);
                            }}
                            placeholder="Search icons..."
                            autoFocus
                        />
                    </div>

                    <div className="icon-picker-content">
                        {/* Show search results or categories */}
                        {filteredIcons ? (
                            // Search results
                            <div className="icon-search-results">
                                {filteredIcons.length === 0 ? (
                                    <div className="no-results">
                                        <FaSearch />
                                        <p>No icons found for "{searchQuery}"</p>
                                    </div>
                                ) : (
                                    <div className="icon-grid">
                                        {filteredIcons.map((icon) => (
                                            <button
                                                key={icon.value}
                                                type="button"
                                                className={`icon-option ${value === icon.value ? 'selected' : ''}`}
                                                onClick={() => handleSelect(icon.value)}
                                                title={icon.label}
                                            >
                                                {React.createElement(getFaIcon(icon.value), { style: { color } })}
                                                <span>{icon.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Category view
                            <div className="icon-categories">
                                {ICON_CATEGORIES.map((category) => (
                                    <div key={category.name} className="icon-category">
                                        <button
                                            type="button"
                                            className={`category-header ${activeCategory === category.name ? 'expanded' : ''}`}
                                            onClick={() => setActiveCategory(
                                                activeCategory === category.name ? null : category.name
                                            )}
                                        >
                                            <span>{category.name}</span>
                                            {activeCategory === category.name ? <FaChevronUp /> : <FaChevronDown />}
                                        </button>
                                        {activeCategory === category.name && (
                                            <div className="icon-grid">
                                                {category.icons.map((icon) => (
                                                    <button
                                                        key={icon.value}
                                                        type="button"
                                                        className={`icon-option ${value === icon.value ? 'selected' : ''}`}
                                                        onClick={() => handleSelect(icon.value)}
                                                        title={icon.label}
                                                    >
                                                        {React.createElement(getFaIcon(icon.value), { style: { color } })}
                                                        <span>{icon.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

IconPicker.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    color: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool
};

export default IconPicker;
